import React, { useEffect, useMemo, useState } from 'react';
import { Card, Modal, Table } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import { fetchRealTimeData } from 'hooks/CallApi';
import { isDemoMode } from 'data/demoMockData';

const MAX_DISPLAY_SERVICES = 6;

const RealTimeUsageChart = ({ serviceType = 'sdk' }) => {
    const [showNamespaceModal, setShowNamespaceModal] = useState(false);
    const [showAllServices, setShowAllServices] = useState(false); // 모달 내 전체보기 토글
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [timeSeriesData, setTimeSeriesData] = useState([]);
    const [totalServiceData, setTotalServiceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [namespaceServiceData, setNamespaceServiceData] = useState({});
    const [viewMode, setViewMode] = useState('latest'); // 'latest' or 'hourly'

    // 컴포넌트 내부에서 services와 namespaces 상태 관리
    const [services, setServices] = useState([]);
    const [namespaces, setNamespaces] = useState([]);

    // 시계열 데이터 관리 (항상 12�� 포인트 = 1시간 동안의 데이터)
    const MAX_DATA_POINTS = 12;
    const DATA_INTERVAL_MINUTES = 5; // 5분 간격으로 데이터 수집

    // 컴포넌트 상단에 zoneId 추출
    const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('zoneId:', zoneId);

    // 시간 포맷팅 함수 - HH:MM 형식
    const formatTime = (date) => {
        return date.getHours().toString().padStart(2, '0') + ':' +
            date.getMinutes().toString().padStart(2, '0');
    };

    // 시간 포맷팅 함수 - HH:MM:SS 형식 (초까지 표시)
    const formatTimeWithSeconds = (date) => {
        return formatTime(date) + ':' +
            date.getSeconds().toString().padStart(2, '0');
    };

    // 가장 가까운 5분 단위 시간을 계산하는 함수 (내림)
    const getRoundedTime = (date = new Date()) => {
        const coeff = 1000 * 60 * DATA_INTERVAL_MINUTES;
        const rounded = new Date(Math.floor(date.getTime() / coeff) * coeff);
        return rounded;
    };

    const generateDemoRealtimeData = () => {
        const now = getRoundedTime();
        const svcNames = serviceType === 'sdk'
            ? ['예/적금', '입출금', '가입자 생성/조회', '대출']
            : serviceType === 'cf'
                ? ['Cloud Function 호출', '배포 관리']
                : ['API Gateway', 'API 요청 처리'];
        const nsNames = ['production', 'staging'];
        const points = [];
        for (let i = MAX_DATA_POINTS - 1; i >= 0; i--) {
            const ts = new Date(now.getTime() - i * DATA_INTERVAL_MINUTES * 60 * 1000);
            const svcUsage = {};
            let total = 0;
            svcNames.forEach(n => {
                const v = Math.floor(Math.random() * 80) + 20;
                svcUsage[n] = v;
                total += v;
            });
            const nsData = {};
            nsNames.forEach(ns => {
                nsData[ns] = {};
                svcNames.forEach(n => { nsData[ns][n] = Math.floor(svcUsage[n] * (ns === 'production' ? 0.7 : 0.3)); });
            });
            points.push({ timestamp: ts.toISOString(), totalUsage: total, serviceUsage: svcUsage, namespaceData: nsData });
        }
        return {
            data: {
                services: svcNames.map(n => ({ name: n })),
                namespaces: nsNames,
                timeSeriesData: points
            }
        };
    };

    // API로부터 실시간 데이터 가져오기 (초기 로드 시 1시간 데이터)
    const loadInitialData = async () => {
        setLoading(true);
        try {
            const currentRoundedTime = getRoundedTime();
            console.debug("첫 로드: 기준 시간", currentRoundedTime.toLocaleTimeString());

            const response = isDemoMode()
                ? generateDemoRealtimeData()
                : await fetchRealTimeData({ hours: 1, interval: DATA_INTERVAL_MINUTES }, zoneId, serviceType);

            console.debug("첫 로드 응답:", response);
            const apiData = response.data || { timeSeriesData: [] };

            // 서비스 및 네임스페이스 정보 저장
            if (apiData.services && Array.isArray(apiData.services)) {
                setServices(apiData.services);
                console.debug("서비스 목록 설정:", apiData.services);
            }

            if (apiData.namespaces && Array.isArray(apiData.namespaces)) {
                setNamespaces(apiData.namespaces);
                console.debug("네임스페이스 목록 설정:", apiData.namespaces);
            }

            // 시계열 데이터 처리
            if (apiData.timeSeriesData && Array.isArray(apiData.timeSeriesData)) {
                const formattedData = apiData.timeSeriesData.map(point => {
                    const timestamp = new Date(point.timestamp);
                    return {
                        time: timestamp,
                        value: point.totalUsage || 0,
                        label: formatTime(timestamp),
                        namespaceServiceData: point.namespaceData || {},
                        serviceUsage: point.serviceUsage || {}
                    };
                });

                console.debug("시계열 데이터 처리:", formattedData.length, "개 포인트");
                setTimeSeriesData(formattedData);

                // 최신 데이터 포인트의 정보 사용
                if (formattedData.length > 0) {
                    const latestData = formattedData[formattedData.length - 1];
                    console.debug("최신 데이터 시간:", latestData.time.toLocaleTimeString());

                    // 네임스페이스 데이터 설정
                    setNamespaceServiceData(latestData.namespaceServiceData);

                    // 서비스별 사용량 설정 (최신 데이터 기준)
                    const updatedServiceData = (apiData.services || []).map(service => ({
                        name: service.name,
                        value: latestData.serviceUsage[service.name] || 0
                    }));
                    setTotalServiceData(updatedServiceData);

                    // 업데이트 시간은 최신 데이터의 타임스탬프 + 5분으로 설정 (해당 간격의 끝 시간)
                    const nextIntervalTime = new Date(latestData.time);
                    nextIntervalTime.setMinutes(nextIntervalTime.getMinutes() + DATA_INTERVAL_MINUTES);
                    setLastUpdated(nextIntervalTime);
                    console.debug("업데이트 시간 설정:", nextIntervalTime.toLocaleTimeString(), "(원래 데이터 시간:", latestData.time.toLocaleTimeString(), "+ 5분)");
                } else {
                    // 데이터가 없는 경우 현재 시간으로 설정
                    setLastUpdated(currentRoundedTime);
                }
            } else {
                // 데이터가 없는 경우 현재 시간으로 설정
                setLastUpdated(currentRoundedTime);
            }
        } catch (err) {
            console.error('실시간 데이터 로드 실패:', err);
            setError('데이터 로드 중 오류가 발생했습니다.');
            // 오류가 있어도 현재 시간 기준으로 설정
            setLastUpdated(getRoundedTime());
        } finally {
            setLoading(false);
        }
    };

    const loadLatestData = async () => {
        setLoading(true);
        try {
            const currentRequestTime = new Date();
            console.debug(`[${currentRequestTime.toLocaleTimeString()}] 실시간 업데이트 요청 시작`);

            let response;
            if (isDemoMode()) {
                const demoFull = generateDemoRealtimeData();
                const latest = demoFull.data.timeSeriesData[demoFull.data.timeSeriesData.length - 1];
                response = { data: { timeSeriesData: [latest] } };
            } else {
                response = await fetchRealTimeData({
                    minutes: DATA_INTERVAL_MINUTES,
                    interval: DATA_INTERVAL_MINUTES,
                    _t: currentRequestTime.getTime()
                }, zoneId, serviceType);
            }

            console.debug('API 응답 수신:', response);
            const apiData = response.data || { timeSeriesData: [] };

            // 최신 데이터가 있는 경우 처리
            if (apiData.timeSeriesData && apiData.timeSeriesData.length > 0) {
                const latestPoint = apiData.timeSeriesData[0];
                const dataTimestamp = new Date(latestPoint.timestamp);

                // 업데이트 시간은 데이터 시간 + 5분으로 설정 (해당 간격의 끝)
                const nextIntervalTime = new Date(dataTimestamp);
                nextIntervalTime.setMinutes(nextIntervalTime.getMinutes() + DATA_INTERVAL_MINUTES);

                console.debug('데이터 기준 시간:', dataTimestamp.toLocaleTimeString());
                console.debug('표시할 업데이트 시간:', nextIntervalTime.toLocaleTimeString());

                // 서비스 사용량 데이터 검사 - JSON.stringify로 확실하게 출력
                const serviceUsageData = latestPoint.serviceUsage || {};
                console.debug("serviceUsage 데이터:", JSON.stringify(serviceUsageData));
                console.debug("serviceUsage 키:", Object.keys(serviceUsageData));
                console.debug("services 배열:", services);

                // 시계열 데이터에 추가
                setTimeSeriesData(prevData => {
                    const newData = [
                        ...prevData,
                        {
                            time: dataTimestamp,
                            value: latestPoint.totalUsage || 0,
                            label: formatTime(dataTimestamp),
                            namespaceServiceData: latestPoint.namespaceData || {},
                            serviceUsage: serviceUsageData
                        }
                    ];
                    return newData.slice(-MAX_DATA_POINTS);
                });

                // 네임스페이스별 서비스 사용량 업데이트
                setNamespaceServiceData(latestPoint.namespaceData || {});

                // 핵심: 직접 서비스 데이터 만들기 (Object.entries 사용)
                if (Object.keys(serviceUsageData).length > 0) {
                    // 서비스 사용량 데이터를 직접 배열로 변환
                    const directServiceData = Object.entries(serviceUsageData).map(([name, value]) => ({
                        name,
                        value
                    }));

                    console.debug("직접 생성한 서비스 데이터:", directServiceData);

                    // 직접 만든 데이터로 업데이트
                    if (directServiceData.length > 0) {
                        setTotalServiceData(directServiceData);
                        console.debug("totalServiceData 상태 업데이트 완료");

                        // 디버그용: 업데이트 후 다시 확인
                        setTimeout(() => {
                            console.debug("업데이트 후 totalServiceData:", totalServiceData);
                        }, 0);
                    }
                } else {
                    console.warn("serviceUsage 데이터가 비어있습니다");
                }

                // 업데이트 시간은 간격의 끝 시간으로 설정
                setLastUpdated(nextIntervalTime);
                console.debug(`업데이트 시간 설정: ${nextIntervalTime.toLocaleTimeString()} (데이터 시간 + 5분)`);
            } else {
                console.warn("API에서 새 데이터를 반환하지 않음");
                setLastUpdated(currentRequestTime);
            }
        } catch (err) {
            console.error('실시간 데이터 업데이트 실패:', err);
            setError('데이터 업데이트 중 오류가 발생했습니다.');
            setLastUpdated(new Date());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setTimeSeriesData([]);
        setTotalServiceData([]);
        setServices([]);
        setNamespaces([]);

        loadInitialData();

        const now = new Date();
        const minutesToNext = 5 - (now.getMinutes() % 5);
        const nextExact = new Date(now);
        nextExact.setMinutes(now.getMinutes() + minutesToNext, 0, 0);

        const delayMs = nextExact.getTime() - now.getTime();
        const shiftMs = 3 * 60 * 1000;

        let timeoutId;
        let intervalId;

        timeoutId = setTimeout(() => {
            loadLatestData();
            intervalId = setInterval(() => {
                loadLatestData();
            }, DATA_INTERVAL_MINUTES * 60 * 1000);
        }, delayMs + shiftMs);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
        };
    }, [serviceType]);


    // 표에서 보여줄 서비스 데이터 (상위 6개)
    const displayedServiceData = useMemo(() => {
        return Array.isArray(totalServiceData)
            ? totalServiceData.slice(0, MAX_DISPLAY_SERVICES)
            : [];
    }, [totalServiceData]);

    const hasMoreServices = totalServiceData.length > MAX_DISPLAY_SERVICES;

    // 모달에서 보여줄 서비스 데이터 (전체 or 상위 6개)
    const modalServiceData = useMemo(() => {
        return showAllServices ? services : services.slice(0, MAX_DISPLAY_SERVICES);
    }, [services, showAllServices]);

    const realTimeChartOptions = useMemo(() => {
        const timeLabels = timeSeriesData.map(point => point.label);
        const values = timeSeriesData.map(point => point.value);

        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const value = params[0].value.toLocaleString();
                    return `${params[0].name}<br/>총 사용량: ${value} 건`;
                }
            },
            grid: {
                left: '-5%',    // 음수 값으로 왼쪽 공간 줄이기 
                right: '2%',    // 오른쪽은 약간의 여백 유지
                bottom: '10%',
                top: '5%',
                containLabel: true // x축 레이블이 잘리지 않게 유지
            },
            xAxis: {
                type: 'category',
                data: timeLabels,
                axisLabel: {
                    color: '#888',
                    fontSize: 10,
                    fontWeight: 'bold',
                },
                axisLine: {
                    lineStyle: {
                        color: 'rgba(0, 0, 0, 0.2)'
                    }
                },
                axisTick: {
                    show: false
                }
            },
            yAxis: {
                type: 'value',
                show: false,
                // 추가 설정으로 더 확실하게 공간 제거
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false }
            },
            series: [{
                name: '실시간 총 사용량',
                type: 'bar', // 라인 차트에서 막대 그래프로 변경
                data: values,
                barWidth: '60%', // 막대 너비 설정
                itemStyle: {
                    color: '#64A9F3'
                },
                emphasis: {
                    itemStyle: {
                        color: '#64A9F3'
                    }
                }
            }]
        };
    }, [timeSeriesData]);

    const handleNamespaceModalOpen = () => {
        setShowNamespaceModal(true);
    };

    const handleNamespaceModalClose = () => {
        setShowNamespaceModal(false);
    };

    // 특정 네임스페이스의 특정 서비스 사��량 가져오기
    const getNamespaceServiceUsage = (namespace, serviceName, isHourly = false) => {
        if (isHourly) {
            return calculateHourlyData[namespace]?.[serviceName] || 0;
        } else {
            if (namespaceServiceData[namespace] &&
                namespaceServiceData[namespace][serviceName] !== undefined) {
                return namespaceServiceData[namespace][serviceName];
            }
            return 0; // 데이터가 없는 경우 0 반환
        }
    };

    // 1시간 누적 데이터 계산
    const calculateHourlyData = useMemo(() => {
        if (!timeSeriesData.length) return {};

        const hourlyData = {};

        // 모든 시계열 데이터 포인트에 대해
        timeSeriesData.forEach(point => {
            // 각 포인트에 저장된 namespaceServiceData 사용
            const pointData = point.namespaceServiceData;

            // 각 네임스페이스별로
            Object.keys(pointData).forEach(namespace => {
                if (!hourlyData[namespace]) hourlyData[namespace] = {};

                // 각 서비스별로
                Object.keys(pointData[namespace]).forEach(service => {
                    if (!hourlyData[namespace][service]) hourlyData[namespace][service] = 0;
                    hourlyData[namespace][service] += pointData[namespace][service];
                });
            });
        });

        return hourlyData;
    }, [timeSeriesData]);

    // 표시할 데이터 결정
    const displayData = useMemo(() => {
        return viewMode === 'latest' ? namespaceServiceData : calculateHourlyData;
    }, [viewMode, namespaceServiceData, calculateHourlyData]);

    return (
        <>
            <Card className="h-100 bg-line-chart-gradient">
                <Card.Header className="bg-transparent text-dark d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">실시간 사용량</h4>
                    <small className="opacity-75">
                        업데이트: {lastUpdated.toLocaleTimeString()}
                        {loading && <span className="ms-2">⟳</span>}
                    </small>
                </Card.Header>
                <Card.Body className="p-0">
                    <div style={{ height: '300px', padding: '0 15px' }}>
                        <ReactECharts
                            option={realTimeChartOptions}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={false}
                        />
                    </div>
                    <div className="list-group-flush p-3">
                        <div className="rounded-2" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
                            <div className="px-3 bg-transparent text-dark d-flex justify-content-between px-0 py-1 fw-semi-bold border-top-0" style={{ borderBottom: '1px solid #eee' }}>
                                <p className="mb-0">서비스</p>
                                <p className="mb-0">사용량</p>
                            </div>
                            {displayedServiceData.length > 0 ? (
                                displayedServiceData.map((service, index) => (
                                    <div key={index} className="px-3 bg-transparent text-dark d-flex justify-content-between px-0 py-1" style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <p className="mb-0">{service.name}</p>
                                        <p className="mb-0">{service.value.toLocaleString()}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="px-3 bg-transparent text-dark d-flex justify-content-center px-0 py-3">
                                    <p className="mb-0 text-muted">데이터가 없음</p>
                                </div>
                            )}
                            {hasMoreServices && (
                                <div className="text-center py-2">
                                    <button className="btn btn-link text-dark p-0" onClick={() => setShowNamespaceModal(true)}>
                                        + 더보기
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </Card.Body>
                <Card.Footer className="text-end bg-transparent">
                    <a className="text-dark" href="#!" onClick={handleNamespaceModalOpen}>
                        네임스페이스별 상세 정보 보기
                        <svg className="svg-inline--fa fa-chevron-right fa-w-10 ms-1 fs-10" aria-hidden="true" focusable="false" data-prefix="fa" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                            <path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c-9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path>
                        </svg>
                    </a>
                </Card.Footer>
                {error && (
                    <div className="position-absolute top-0 start-50 translate-middle-x mt-2">
                        <div className="badge bg-danger">
                            {error}
                        </div>
                    </div>
                )}
            </Card>

            {/* 네임스페이스별 상세 정보 모달 */}
            <Modal
                show={showNamespaceModal}
                onHide={() => setShowNamespaceModal(false)}
                centered
                size="lg"
            // style={{ maxWidth: '1100px', width: '90%' }} // 모달 자체의 최대 크기 확장
            >
                <Modal.Header closeButton>
                    <Modal.Title>네임스페이스별 실시간 서비스 현황</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between mb-3 align-items-center">
                        <div>
                            <span className="badge bg-primary">
                                표시 중인 데이터: 최근 1시간 (5분 단위)
                            </span>
                            {timeSeriesData.length > 0 && (
                                <span className="badge bg-info ms-2">
                                    최신 데이터: {formatTime(timeSeriesData[timeSeriesData.length - 1].time)} ~ {formatTimeWithSeconds(
                                        new Date(timeSeriesData[timeSeriesData.length - 1].time.getTime() + 5 * 60 * 1000 - 1)
                                    )}
                                </span>
                            )}
                        </div>
                        <small className="text-muted">
                            업데이트: {lastUpdated.toLocaleTimeString()}
                        </small>
                    </div>

                    <div className="btn-group mb-3">
                        <button
                            className={`btn ${viewMode === 'latest' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('latest')}
                        >
                            최근 5분 데이터
                        </button>
                        <button
                            className={`btn ${viewMode === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('hourly')}
                        >
                            1시간 누적 데이터
                        </button>
                    </div>
                    <div className="mb-2 text-end">
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        {/* 네임스페이스별 카드형 서비스 표 */}
                        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                            <div className="row">
                                {namespaces.map((namespace, nsIdx) => {
                                    // 항상 전체 서비스 기준으로!
                                    const nsServices = services.filter(
                                        service => displayData[namespace]?.[service.name] !== undefined
                                    );
                                    return (
                                        <div key={nsIdx} className="col-md-4 mb-3">
                                            <div className="card h-100">
                                                <div className="card-header text-center fw-bold"
                                                    style={{
                                                        background: '#f5f7fa',
                                                        minHeight: 56,      // 높이 고정 (원하는 값으로 조절, 예: 56px)
                                                        height: 56,         // 높이 고정
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 15,       // 폰트 크기 크게
                                                        letterSpacing: '0.5px',
                                                        whiteSpace: 'nowrap',           // 한 줄로 고정
                                                        overflow: 'hidden',             // 넘치면 숨김
                                                        textOverflow: 'ellipsis'        // ... 처리
                                                    }}>
                                                    {namespace}
                                                </div>
                                                <div style={{ maxHeight: 320, overflowY: nsServices.length > 6 ? 'auto' : 'visible' }}>
                                                    <ul className="list-group list-group-flush">
                                                        {nsServices.map((service, idx) => (
                                                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                                                <span>{service.name}</span>
                                                                <span>{(displayData[namespace]?.[service.name] ?? 0).toLocaleString()}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default RealTimeUsageChart;